/*
 * @Description: 实时定位驾驶舱 — 常量与本地配置
 */
import { PoiIconType } from './types/poi.types'
import {
  AlertRule,
  AlertRulesDraft,
  AlertRuleType,
  LocationMode,
  LocationSettings
} from './types/settings.types'

// webpack DefinePlugin 注入的构建环境（config/webpack.config.js）
declare const __BUILD_ENV__: string

/** POI 点位上限（接口文档 E：硬性 50/团队） */
export const POI_MAX_COUNT = 50

/** POI 预设图标分类（设计图：客户/巡检点/餐饮/工地 + 自定义上传） */
export interface PoiIconMeta {
  key: PoiIconType
  label: string
  /** 圆形底色 */
  color: string
  /** 该预设图标的图片 url（自包含 data-uri，选中即作为 iconUrl 存储/渲染） */
  iconUrl: string
}

/**
 * 4 个预设分类（自定义 custom 走上传，不在此列）。
 * iconUrl = 美术正式图标（OSS）；批量导入默认图标取数组首项「客户」。
 */
export const POI_ICON_TYPES: PoiIconMeta[] = [
  {
    key: 'customer',
    label: '客户',
    color: '#faad14',
    iconUrl:
      'https://net-cloud.xhey.top/data/006f43ee-3067-459f-b3a2-c9924f5ce2e3.png'
  },
  {
    key: 'patrol',
    label: '巡检点',
    color: '#52c41a',
    iconUrl:
      'https://net-cloud.xhey.top/data/1ee39125-3bed-40ba-bf1e-cb7a183b8596.png'
  },
  {
    key: 'catering',
    label: '餐饮',
    color: '#fa8c16',
    iconUrl:
      'https://net-cloud.xhey.top/data/f29c8b06-de87-40f1-ade2-6304cfba4b8f.png'
  },
  {
    key: 'site',
    label: '工地',
    color: '#722ed1',
    iconUrl:
      'https://net-cloud.xhey.top/data/df361e30-d1f1-41e2-981d-0976e1f059e1.png'
  }
]

/**
 * 地图显示要素层（AMap.Map features）。
 * 去掉默认的 `point`（高德自带兴趣点文字标注），只留背景/道路/建筑，让轨迹与点位更突出。
 */
export const MAP_FEATURES = ['bg', 'road', 'building']

/** 含 point 的全要素（普通地图：显示高德兴趣点文字标注） */
export const MAP_FEATURES_FULL = ['bg', 'road', 'building', 'point']

/**
 * 地图样式：纯净（默认，whitesmoke 无 point）/ 普通（normal 含 point）/ 卫星。
 */
export type CockpitMapStyle = 'clean' | 'standard' | 'satellite'

export const MAP_STYLE_OPTIONS: Array<{
  value: CockpitMapStyle
  label: string
}> = [
  { value: 'clean', label: '纯净地图' },
  { value: 'standard', label: '普通地图' },
  { value: 'satellite', label: '卫星地图' }
]

/** 实时态轮询间隔（接口文档 4.5：前端 30–60s 轮询，取 60s） */
export const REALTIME_POLL_INTERVAL_MS = 60 * 1000

/** 顶部刷新频率：预设选项（10 秒 / 30 秒 / 1 分钟） */
export const REFRESH_INTERVAL_OPTIONS: Array<{
  value: number
  label: string
}> = [
  { value: 10 * 1000, label: '10秒' },
  { value: 30 * 1000, label: '30秒' },
  { value: 60 * 1000, label: '1分钟' }
]

/** 刷新频率默认值（1 分钟） */
export const DEFAULT_REFRESH_INTERVAL_MS = 60 * 1000

/** 自定义刷新频率下限（10 秒） */
export const CUSTOM_REFRESH_MIN_SEC = 10

/** 自定义刷新频率上限（1 小时） */
export const CUSTOM_REFRESH_MAX_SEC = 60 * 60

/** 轨迹线配色：各成员按此顺序轮流取色（index % 长度） */
export const TRACK_COLORS = [
  '#2F80ED',
  '#00B8D9',
  '#8B5CF6',
  '#FF9F43',
  '#FF5C8A',
  '#22C55E'
]

/** 选中成员的轨迹高亮色 */
export const SELECTED_TRACK_COLOR = '#26A12C'

/**
 * mock 模式下的「当前时刻」基准（对齐 mock 数据的最新轨迹点），
 * 使时间轴范围与拖拽快照和 mock 数据自洽；真实模式用 Date.now()。
 */
export const MOCK_NOW_MS = 1780907268090

/** 底部「30 分钟轨迹」时长选项（接口文档：B1 跨度 ≤ 24h） */
export const TRACK_DURATION_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 30 * 60 * 1000, label: '30 分钟轨迹' },
  { value: 60 * 60 * 1000, label: '1 小时轨迹' },
  { value: 3 * 60 * 60 * 1000, label: '3 小时轨迹' },
  { value: 24 * 60 * 60 * 1000, label: '24 小时轨迹' }
]

/** 默认轨迹时长：30 分钟 */
export const DEFAULT_TRACK_DURATION_MS = 30 * 60 * 1000

/**
 * 状态统计/筛选的状态键（实时态，对齐接口文档 A1 的桶划分）。
 * abnormal（异常）的成员归属来自 D/alerts（未结告警），非 A3 自带字段。
 */
export const STATUS_KEYS = {
  ONLINE: 'online',
  MOVING: 'moving',
  STAYING: 'staying',
  OFFLINE: 'offline',
  ABNORMAL: 'abnormal'
} as const

export type StatusKey = (typeof STATUS_KEYS)[keyof typeof STATUS_KEYS]

/**
 * 团队成员卡「绑定筛选」下拉值。
 * 全部/已绑定/未绑定来自 A2 assigned；在线来自 A2 online；
 * 移动中/停留中需关联 A3 locationState（实时态有效）。
 */
export type MemberBindFilter =
  | 'all'
  | 'bound'
  | 'unbound'
  | 'online'
  | 'moving'
  | 'staying'

export const MEMBER_BIND_FILTER_OPTIONS: Array<{
  value: MemberBindFilter
  label: string
}> = [
  { value: 'all', label: '全部' },
  { value: 'bound', label: '已绑定' },
  { value: 'unbound', label: '未绑定' },
  { value: 'online', label: '在线' },
  { value: 'moving', label: '移动中' },
  { value: 'staying', label: '停留中' }
]

/* ============================ 定位设置（采集 + 异常提醒） ============================ */

/** 采集时段时间选择的分钟步长（对齐 ai_device_web 的 5 分钟轮） */
export const WORK_TIME_MINUTE_STEP = 5

/** 工作日预设方案值（对齐 ai_device_web workdayPresets） */
export type WorkdayPreset = 'mon-fri' | 'mon-sat' | 'everyday' | 'custom'

/** 工作日预设下拉项 */
export const WORKDAY_PRESETS: Array<{ value: WorkdayPreset; label: string }> = [
  { value: 'mon-fri', label: '周一至周五' },
  { value: 'mon-sat', label: '周一至周六' },
  { value: 'everyday', label: '每天' },
  { value: 'custom', label: '自定义' }
]

/** 自定义工作日 7 项（value = 后端 ISO 串，周一=1 … 周日=7） */
export const WEEK_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '1', label: '周一' },
  { value: '2', label: '周二' },
  { value: '3', label: '周三' },
  { value: '4', label: '周四' },
  { value: '5', label: '周五' },
  { value: '6', label: '周六' },
  { value: '7', label: '周日' }
]

/** 预设方案 → workDays 数组（对齐 ai_device_web presetWorkDays，custom 不在内） */
export const PRESET_WORK_DAYS: Record<
  Exclude<WorkdayPreset, 'custom'>,
  string[]
> = {
  'mon-fri': ['1', '2', '3', '4', '5'],
  'mon-sat': ['1', '2', '3', '4', '5', '6'],
  everyday: ['1', '2', '3', '4', '5', '6', '7']
}

/**
 * workDays 串 → 去重升序的有效天数组（只保留 1-7）。
 * 对齐 ai_device_web normalizeWorkDays。
 */
export function normalizeWorkDays(workDays: string): string[] {
  const days = String(workDays || '')
    .split(',')
    .map((d) => d.trim())
    .filter((d) => /^[1-7]$/.test(d))
  return Array.from(new Set(days)).sort((a, b) => Number(a) - Number(b))
}

/** 天数组 → 命中的预设方案；无命中返回 'custom'（对齐 getWorkdayPreset） */
export function getWorkdayPreset(days: string[]): WorkdayPreset {
  const key = days.join(',')
  const hit = (
    Object.keys(PRESET_WORK_DAYS) as Array<Exclude<WorkdayPreset, 'custom'>>
  ).find((k) => PRESET_WORK_DAYS[k].join(',') === key)
  return hit || 'custom'
}

/** 预设 + 自定义天 → workDays 串（对齐 getSelectedWorkDays） */
export function buildWorkDays(
  preset: WorkdayPreset,
  customDays: string[]
): string {
  const days = preset === 'custom' ? customDays : PRESET_WORK_DAYS[preset]
  return normalizeWorkDays((days || []).join(',')).join(',')
}

/** 天数组 → 顿号文案（'周一、周三'）；空返回占位 */
export function workdayCustomLabel(days: string[]): string {
  const sorted = normalizeWorkDays(days.join(','))
  return (
    WEEK_OPTIONS.filter((o) => sorted.includes(o.value))
      .map((o) => o.label)
      .join('、') || '请选择'
  )
}

/** 采集定位模式选项（对齐 ai_device_web LOCATION_MODE_OPTIONS） */
export const LOCATION_MODE_OPTIONS: Array<{
  value: LocationMode
  label: string
}> = [
  { value: 'walk', label: '步行模式' },
  { value: 'vehicle', label: '车辆模式' },
  { value: 'mixed', label: '混合模式' }
]

/** 定位设置默认值（G1-2 六字段；接口无数据/读取失败时的兜底） */
export const DEFAULT_LOCATION_SETTINGS: LocationSettings = {
  workStartTime: '08:00',
  workEndTime: '18:00',
  workDays: '1,2,3,4,5',
  pauseOnHoliday: false,
  locationMode: 'walk',
  allowMemberViewOwnTrack: false
}

/** 连续移动超距 触发距离范围（米，对齐 ai_device_web DistanceSettingView） */
export const CONTINUOUS_DISTANCE_RANGE = { min: 100, max: 2000 }

/** 停留时长超限 限制时长范围（分钟，对齐 ai_device_web StaySettingView） */
export const STAY_TIMEOUT_RANGE = { min: 5, max: 240 }

/**
 * 异常提醒默认值（G3 缺某 ruleType 时的兜底）。
 * 参数缺省对齐接口文档 G3/G4 schema（continuous_distance/stay_timeout）。
 */
export const DEFAULT_ALERT_RULES: AlertRulesDraft = {
  continuousDistanceEnabled: true,
  continuousDistanceMeters: 500,
  continuousDistanceWindowMinutes: 5,
  stayTimeoutEnabled: true,
  stayTimeoutMinutes: 30,
  stayTimeoutRadiusMeters: 50,
  offlineNotifyEnabled: true
}

/** 安全解析 paramsJson（坏串/空串兜底为空对象） */
function parseParams(json: string): Record<string, unknown> {
  try {
    const o = JSON.parse(json || '{}')
    return o && typeof o === 'object' ? (o as Record<string, unknown>) : {}
  } catch (e) {
    return {}
  }
}

/** 数值兜底：非有限数取 fallback */
function toNum(v: unknown, fallback: number): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

/**
 * G3 items → 扁平 UI 草稿。
 * 缺失的 ruleType 用 DEFAULT_ALERT_RULES 兜底；window_minutes/radius_meters 原样保留。
 */
export function parseAlertRules(items: AlertRule[]): AlertRulesDraft {
  const d: AlertRulesDraft = { ...DEFAULT_ALERT_RULES }
  ;(items || []).forEach((rule) => {
    const p = parseParams(rule.paramsJson)
    if (rule.ruleType === 'continuous_distance') {
      d.continuousDistanceEnabled = !!rule.enabled
      d.continuousDistanceMeters = toNum(
        p.meters,
        DEFAULT_ALERT_RULES.continuousDistanceMeters
      )
      d.continuousDistanceWindowMinutes = toNum(
        p.window_minutes,
        DEFAULT_ALERT_RULES.continuousDistanceWindowMinutes
      )
    } else if (rule.ruleType === 'stay_timeout') {
      d.stayTimeoutEnabled = !!rule.enabled
      d.stayTimeoutMinutes = toNum(
        p.minutes,
        DEFAULT_ALERT_RULES.stayTimeoutMinutes
      )
      d.stayTimeoutRadiusMeters = toNum(
        p.radius_meters,
        DEFAULT_ALERT_RULES.stayTimeoutRadiusMeters
      )
    } else if (rule.ruleType === 'offline') {
      d.offlineNotifyEnabled = !!rule.enabled
    }
  })
  return d
}

/** G4 单条保存载荷结构 */
export interface AlertRuleSave {
  ruleType: AlertRuleType
  paramsJson: string
  enabled: boolean
}

/**
 * UI 草稿 → G4 保存载荷（3 条：continuous_distance / stay_timeout / offline）。
 * offline 仅开关，paramsJson 传 '{}'（服务端忽略）。
 */
export function buildAlertRuleSaves(d: AlertRulesDraft): AlertRuleSave[] {
  return [
    {
      ruleType: 'continuous_distance',
      enabled: d.continuousDistanceEnabled,
      paramsJson: JSON.stringify({
        window_minutes: d.continuousDistanceWindowMinutes,
        meters: d.continuousDistanceMeters
      })
    },
    {
      ruleType: 'stay_timeout',
      enabled: d.stayTimeoutEnabled,
      paramsJson: JSON.stringify({
        minutes: d.stayTimeoutMinutes,
        radius_meters: d.stayTimeoutRadiusMeters
      })
    },
    { ruleType: 'offline', enabled: d.offlineNotifyEnabled, paramsJson: '{}' }
  ]
}

/**
 * 是否启用本地 mock 数据。
 *
 * 默认策略（接口已部署，默认走真实端点）：
 * - 所有环境默认关闭 mock，调用真实 /web/location/cockpit 接口
 *
 * 显式覆盖（任意环境，优先级高于默认）：
 * - URL `?mockCockpit=1` 强制开 mock / `?mockCockpit=0` 强制关
 * - localStorage `COCKPIT_MOCK=1` 强制开 mock / `=0` 强制关
 *
 * @returns 命中开启条件返回 true
 */
export function isCockpitMockEnabled(): boolean {
  // window / localStorage 在异常环境（隐私模式等）可能抛错，兜底返回 false
  try {
    const param = new URLSearchParams(window.location.search).get('mockCockpit')
    if (param === '1') return true
    if (param === '0') return false

    const stored = window.localStorage.getItem('COCKPIT_MOCK')
    if (stored === '1') return true
    if (stored === '0') return false

    return false
  } catch (e) {
    return false
  }
}
